import React, { useEffect, useState, useCallback } from "react";
import "./index.css";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// REDUX Imports
import { setShowReloginModal } from "../../../redux/features/login/login";
import { toast_position } from "../../../redux/features/Settings/Authorization";
import {
  initializeTotp,
  removeInitialError,
  resetDup,
} from "../../../redux/features/totp/totp";
import { encryptCloudKeys } from "../../../redux/features/byok/byok";

// COMPONENTS Imports
import ICONS from "../../../assets/Icons";
import NavbarBoxComponent from "../../../components/_utils/NavbarBoxComponent";
import UniversalRefreshToast from "../../../components/_utils/UniversalRefreshToast";

const SettingsSecurityCenter = () => {
  // useDispatch
  const dispatch = useDispatch();

  // useNavigate
  const navigate = useNavigate();
  const location = useLocation();

  // useState
  const [cloudKey, setCloudKeys] = useState(false);

  // useSelector
  const services = useSelector((state) => state.refreshToken.services) || [];
  let userId;
  try {
    const storedResponse = sessionStorage.getItem("response");
    userId = storedResponse
      ? JSON.parse(storedResponse)?.employees?.id
      : undefined;
  } catch {
    userId = undefined;
  }

  const { initializeTotpErrorMsgDup } = useSelector((state) => state.totp);

  // useCallback
  const ensureSession = useCallback(async () => {
    const token = sessionStorage.getItem("q2p_token");
    const sessionStatus = sessionStorage.getItem("sessionStatus");
    if (!token || sessionStatus === "false") {
      sessionStorage.removeItem("loggedIn");
      if (!sessionStorage.getItem("previousLog")) {
        sessionStorage.setItem("previousLog", location?.pathname || "/home");
      }
      dispatch(setShowReloginModal());
      navigate("/relogin", { replace: true });
      return false;
    }

    return true;
  }, [dispatch, navigate, location?.pathname]);

  // useEffect
  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      const ok = await ensureSession();
      if (!ok) return;
      if (!userId) return;
      if (sessionStorage.getItem("sessionStatus") === "false") return;

      const response = await dispatch(encryptCloudKeys(""));
      if (isMounted) {
        setCloudKeys(response?.payload);
      }
    };

    init();

    return () => {
      isMounted = false;
      dispatch(resetDup());
    };
  }, [dispatch, ensureSession, userId]);

  useEffect(() => {
    const handlePopState = () => {
      ensureSession();
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [ensureSession]);

  const buttonConfigSecurity = [
    {
      title: "SECURITY CENTER",
      items: [
        {
          label: (
            <div className="text-font-sm font-normal text-text-primary">
              TOTP
              <span
                className={`text-font-sm ml-1 ${
                  initializeTotpErrorMsgDup ===
                  "Two Factor Authentication is already enabled"
                    ? "font-semibold"
                    : "font-normal"
                }`}
              >
                (
              </span>
              <span
                className={`text-font-sm ${
                  initializeTotpErrorMsgDup ===
                  "Two Factor Authentication is already enabled"
                    ? "font-semibold"
                    : "font-normal"
                }`}
                style={{
                  color:
                    initializeTotpErrorMsgDup ===
                    "Two Factor Authentication is already enabled"
                      ? "var(--settled)"
                      : "",
                }}
              >
                {initializeTotpErrorMsgDup ===
                "Two Factor Authentication is already enabled"
                  ? "Active"
                  : "Recommended"}
              </span>
              <span
                className={`text-font-sm ${
                  initializeTotpErrorMsgDup ===
                  "Two Factor Authentication is already enabled"
                    ? "font-semibold"
                    : "font-normal"
                }`}
              >
                )
              </span>
            </div>
          ),
          icon: ICONS.TOTP,
          route: "/settings/security_center/TOTP_Setup",
          requiredService: ["PLogin"],
        },
        {
          label: (
            <div className="text-font-sm font-normal text-text-primary flex">
              BYOK{" "}
              <span
                className={`text-font-sm ml-1 ${
                  cloudKey?.message === "Data is encrypted successfully."
                    ? "font-semibold"
                    : "font-normal"
                }`}
                style={{
                  display:
                    cloudKey?.message === "Data is encrypted successfully."
                      ? "block"
                      : "none",
                }}
              >
                (
              </span>
              <span
                className={`text-font-sm ${
                  cloudKey?.message === "Data is encrypted successfully."
                    ? "font-semibold"
                    : "font-normal"
                }`}
                style={{
                  color:
                    cloudKey?.message === "Data is encrypted successfully."
                      ? "var(--settled)"
                      : "",
                }}
              >
                {cloudKey?.message === "Data is encrypted successfully."
                  ? "Active"
                  : ""}
              </span>
              <span
                className={`text-font-sm ${
                  cloudKey?.message === "Data is encrypted successfully."
                    ? "font-semibold"
                    : "font-normal"
                }`}
                style={{
                  display:
                    cloudKey?.message === "Data is encrypted successfully."
                      ? "block"
                      : "none",
                }}
              >
                )
              </span>
            </div>
          ),
          icon: ICONS.BYOK,
          route: "/settings/security_center/BYOK",
          requiredService: [
            "UCredentials",
            "CCredentials",
            "EEncryption",
            "VCredentials",
          ],
        },
        {
          label: "Change Password",
          icon: ICONS.CHANGE_PASSWORD,
          route: "/settings/security_center/change_password",
          requiredService: ["CPwd"],
        },
      ],
    },
  ];

  const isMobile = false;

  const handleNavigate = async (type, requiredService, route) => {
    const ok = await ensureSession();
    if (!ok) return;

    const hasServices = requiredService.every((service) =>
      services.includes(service),
    );
    if (!hasServices) {
      navigate("/feature_not_assigned");
    } else if (type === "Change Password") {
      navigate("/settings/security_center/change_password");
    } else if (type === "TOTP (Recommended)") {
      if (isMobile) {
        toast.info("coming soon!", toast_position);
        return;
      }
      UniversalRefreshToast("initializeTotp");
      dispatch(removeInitialError());
      dispatch(initializeTotp({ id: userId, route, navigate }));
    } else {
      navigate(route);
    }
  };

  const renderSecurityButtons = (buttons) =>
    buttons.map((button, buttonIndex) => {
      const { label, icon, route, requiredService } = button;
      const isLastButton = buttonIndex === buttons.length - 1;
      const hasService = requiredService.every((service) =>
        services.includes(service),
      );

      return (
        <>
          <NavbarBoxComponent
            iconRight={ICONS.ARROW_RIGHT_WHITE}
            iconLeft={icon}
            title={label}
            handleFunction={() => {
              handleNavigate(label, requiredService, route);
            }}
            isDisabled={
              !(
                hasService ||
                services?.includes("PLogin") ||
                services?.includes("CPwd")
              )
            }
          />

          <div
            className={`w-full ${isLastButton ? "hidden" : "block"} border border-primary-act`}
          />
        </>
      );
    });

  return (
    <div className="h-screen  overflow-auto lg:overflow-hidden">
      <div className="lg:hidden fixed w-full top-top-base bg-custom-card z-z-index-content flex items-center cursor-pointer">
        <div
          onClick={async () => {
            const ok = await ensureSession();
            if (ok) navigate(-1);
          }}
        >
          <img
            src={ICONS.CLOSE}
            alt="Close"
            className="w-icon-md h-icon-md m-margin-md"
          />
        </div>
        <h1 className="text-base flex-1 text-text-primary">Security center</h1>
      </div>
      <div className="h-navbar-breadcrumb-height w-full  flex-shrink-0 Qsmd:flex hidden"></div>
      <div className="w-full Qsmd:px-padding-base px-padding-sm max-w-tab-width-normal mx-auto">
        <div className="flex justify-between Qsmd:mt-margin-xl mt-margin-base Qsmd:pt-padding-base pt-[60px]">
          <div className="overflow-hidden self-start w-full h-full Qsmd:pt-padding-base pt-padding-md">
            {buttonConfigSecurity.map((data, index) => (
              <div
                key={index}
                className="Qsmd:w-navbar-box-component w-full flex flex-col gap-gap-lg h-full"
              >
                <h2 className="text-text-primary text-font-md font-semibold uppercase">
                  {data.title}
                </h2>

                <div className="flex flex-col gap-gap-xs">
                  {renderSecurityButtons(data.items)}
                </div>
              </div>
            ))}
          </div>

          <div className="hidden sm:flex mx-auto my-auto Qsm:pt-padding-md Qmd:pt-padding-base">
            <img
              src={ICONS?.SECURITY}
              alt="Settings"
              className="h-graphic_rectangle_height_xl w-graphic-width-lg flex-shrink-0"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsSecurityCenter;
